/**
 * Deal-type party vocabulary.
 *
 * One pair of tables — `vendors` and `purchasers` — holds both halves of every deal,
 * and which half a row plays is stored on the row: `vendor_type` is VENDOR|LANDLORD
 * and `purchaser_type` is PURCHASER|TENANT. So a tenancy's parties live in the
 * vendor / purchaser tables while being, in every document and every conversation,
 * a landlord and a tenant. Showing the table's name is wrong on a rental — a tenancy
 * has no vendor.
 *
 * Only the words change. Payload keys, request bodies and picker `type` tokens stay
 * VENDOR / PURCHASER throughout.
 *
 * Takes either the raw enum (`RENT`, from the sales list row) or the display label
 * the server already humanises (`Rent`, on the sale detail payload) — both surfaces
 * ask the same question and neither should have to care which one it is holding.
 */
export function partyWords(dealType) {
  const rent = String(dealType || '').toUpperCase() === 'RENT';
  return rent
    ? {
        isRent: true,
        vendor: 'Landlord',
        vendors: 'Landlords',
        purchaser: 'Tenant',
        purchasers: 'Tenants',
        vendorType: 'LANDLORD',
        purchaserType: 'TENANT',
      }
    : {
        isRent: false,
        vendor: 'Vendor',
        vendors: 'Vendors',
        purchaser: 'Purchaser',
        purchasers: 'Purchasers',
        vendorType: 'VENDOR',
        purchaserType: 'PURCHASER',
      };
}

/**
 * Both words, for the places that span deal types and cannot pick one — a filter over
 * every sale, a contact registry that holds landlords and vendors side by side.
 */
export const VENDOR_ANY = 'Vendor / Landlord';
export const PURCHASER_ANY = 'Purchaser / Tenant';
